import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  ClipboardList, Plus, Calendar, Clock, CheckCircle, Loader2, User, Eye, Trash2, Users, Send,
  FileText, Upload, Download, X, Edit, ArrowUpDown, ArrowUp, ArrowDown, Archive, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('fortis_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const BranchTasks2 = () => {
  const [mainTab, setMainTab] = useState('daily');
  const [tasks, setTasks] = useState([]);
  const [internalTasks, setInternalTasks] = useState([]); // Şube İçi Görevler
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  
  // Progress modal state
  const [progressModalOpen, setProgressModalOpen] = useState(false);
  const [progressLogs, setProgressLogs] = useState([]);
  const [newProgressLog, setNewProgressLog] = useState({ description: '', documents: [] });
  
  // Complete modal state
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [completionNotes, setCompletionNotes] = useState('');
  
  // Finish notification modal state (başkasının görevi için)
  const [finishNotifyModalOpen, setFinishNotifyModalOpen] = useState(false);
  const [finishNotifyNotes, setFinishNotifyNotes] = useState('');
  
  // Bildirim sistemi state'leri
  const [unreadInternalTaskIds, setUnreadInternalTaskIds] = useState(new Set()); // Şube İçi Görevlerde okunmamış
  const [internalTaskNotifications, setInternalTaskNotifications] = useState([]); // Şube İçi Görev bildirimleri
  
  // Arşiv state'leri
  const [archivedTasks, setArchivedTasks] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  
  // Current user
  const user = JSON.parse(localStorage.getItem('fortis_user') || '{}');
  
  // Preview state
  const [previewDoc, setPreviewDoc] = useState(null);
  
  // Çoklu seçim state'leri
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Sıralama state'i
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  // Şube İçi Görevler sıralama state'i
  const [internalSortColumn, setInternalSortColumn] = useState('created_at');
  const [internalSortDirection, setInternalSortDirection] = useState('desc');
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assigned_to_ids: [], // Çoklu seçim için array
    task_type: 'daily',
    priority: 'normal',
    due_date: new Date().toISOString().split('T')[0],
    documents: []
  });

  useEffect(() => {
    loadData();
    loadArchivedTasks();
  }, [mainTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Şube görevleri + İş paneli görevleri birlikte
      const [branchTasksRes, workTasksRes, empRes, internalRes] = await Promise.all([
        axios.get(`${API}/branch/tasks`, { 
          params: { task_type: mainTab },
          headers: getAuthHeaders() 
        }).catch(() => ({ data: [] })),
        axios.get(`${API}/work/gorevler`, { 
          params: { task_type: mainTab },
          headers: getAuthHeaders() 
        }).catch(() => ({ data: [] })),
        axios.get(`${API}/branch/employees`, { headers: getAuthHeaders() }),
        axios.get(`${API}/branch/internal-tasks`, { headers: getAuthHeaders() }).catch(() => ({ data: [] }))
      ]);
      
      // Görevleri birleştir ve tekrarları kaldır
      const allTasks = [...(branchTasksRes.data || []), ...(workTasksRes.data || [])];
      const uniqueTasks = allTasks.filter((task, index, self) => 
        index === self.findIndex(t => t.id === task.id)
      );
      
      setTasks(uniqueTasks);
      setEmployees(empRes.data || []);
      setInternalTasks(internalRes.data || []);
    } catch (error) {
      console.error('Veri yüklenirken hata:', error);
    }
    setLoading(false);
  };

  // Şube İçi Görev bildirimleri yükle
  const loadInternalTaskNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications`, {
        params: { category: 'sube_ici_gorev', is_read: false },
        headers: getAuthHeaders()
      });
      const notifications = res.data || [];
      setInternalTaskNotifications(notifications);
      
      // Okunmamış bildirimi olan görev ID'lerini topla
      const ids = new Set(notifications.map(n => n.related_id).filter(Boolean));
      setUnreadInternalTaskIds(ids);
    } catch (error) {
      console.error('Şube içi görev bildirimleri yüklenemedi:', error);
    }
  };

  // Günlük/Aylık Görev bildirimleri yükle (gorevler kategorisi)
  const [gorevlerNotifications, setGorevlerNotifications] = useState([]);
  const [unreadGorevlerIds, setUnreadGorevlerIds] = useState(new Set());

  // Sayfa yüklendiğinde badge'i sıfırla
  useEffect(() => {
    const markTasksAsSeen = async () => {
      try {
        await axios.post(`${API}/badges/mark-seen`, {
          panel: 'branch',
          menu: 'tasks',
          tab: 'daily',
          sub_tab: ''
        }, { headers: getAuthHeaders() });
        
        // Badge güncellemesi için event dispatch et
        window.dispatchEvent(new Event('unread-counts-updated'));
      } catch (error) {
        console.error('Badge güncelleme hatası:', error);
      }
    };
    
    markTasksAsSeen();
  }, []);

  const loadGorevlerNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications`, {
        params: { category: 'gorevler', is_read: false },
        headers: getAuthHeaders()
      });
      const notifications = res.data || [];
      setGorevlerNotifications(notifications);
      
      // Okunmamış bildirimi olan görev ID'lerini topla
      const ids = new Set(notifications.map(n => n.related_id).filter(Boolean));
      setUnreadGorevlerIds(ids);
    } catch (error) {
      console.error('Görev bildirimleri yüklenemedi:', error);
    }
  };

  // Görev bildirimini okundu işaretle
  const markGorevAsRead = async (taskId) => {
    try {
      const relatedNotifs = gorevlerNotifications.filter(n => n.related_id === taskId);
      for (const notif of relatedNotifs) {
        await axios.put(`${API}/notifications/${notif.id}/read`, {}, { headers: getAuthHeaders() });
      }
      
      setUnreadGorevlerIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(taskId);
        return newSet;
      });
      
      window.dispatchEvent(new Event('unread-counts-updated'));
    } catch (error) {
      console.error('Bildirim okundu işaretlenemedi:', error);
    }
  };

  // Bildirimi okundu işaretle
  const markInternalTaskAsRead = async (taskId) => {
    try {
      // Bu görevle ilgili tüm okunmamış bildirimleri bul ve okundu işaretle
      const relatedNotifs = internalTaskNotifications.filter(n => n.related_id === taskId);
      for (const notif of relatedNotifs) {
        await axios.put(`${API}/notifications/${notif.id}/read`, {}, { headers: getAuthHeaders() });
      }
      
      // State'i güncelle
      setUnreadInternalTaskIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(taskId);
        return newSet;
      });
      
      // Global bildirim sayısını güncelle
      window.dispatchEvent(new Event('unread-counts-updated'));
    } catch (error) {
      console.error('Bildirim okundu işaretlenemedi:', error);
    }
  };

  // Arşivlenmiş görevleri yükle
  const loadArchivedTasks = async () => {
    try {
      const res = await axios.get(`${API}/branch/tasks/archived`, {
        params: { task_type: mainTab },
        headers: getAuthHeaders()
      });
      setArchivedTasks(res.data || []);
    } catch (error) {
      console.error('Arşivlenmiş görevler yüklenemedi:', error);
    }
  };

  // File size formatter
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Check if file is previewable
  const isPreviewable = (type) => {
    return type && (type.startsWith('image/') || type === 'application/pdf');
  };

  // Task file upload handler
  const handleTaskFileUpload = (event) => {
    const files = Array.from(event.target.files);
    files.forEach(file => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Dosya boyutu 10MB\'dan büyük olamaz');
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData(prev => ({
          ...prev,
          documents: [...prev.documents, {
            name: file.name,
            type: file.type,
            size: file.size,
            data: e.target.result
          }]
        }));
      };
      reader.readAsDataURL(file);
    });
    event.target.value = '';
  };

  // Remove task document
  const removeTaskDocument = (index) => {
    setFormData(prev => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index)
    }));
  };

  // Görevi arşivle
  const handleArchiveTask = async (taskId) => {
    try {
      const response = await axios.put(`${API}/branch/tasks/${taskId}/archive`, {}, { headers: getAuthHeaders() });
      if (response.status === 200) {
        toast.success('Görev arşivlendi');
        // Reload işlemlerini ayrı try-catch içinde yap
        try {
          await Promise.all([loadTasks(), loadArchivedTasks()]);
        } catch (e) {
          console.log('Liste yenileme hatası:', e);
        }
      }
    } catch (error) {
      console.error('Arşivleme hatası:', error);
      toast.error('Görev arşivlenemedi');
    }
  };

  // Görevi arşivden geri yükle
  const handleRestoreTask = async (taskId) => {
    try {
      const response = await axios.put(`${API}/branch/tasks/${taskId}/restore`, {}, { headers: getAuthHeaders() });
      if (response.status === 200) {
        toast.success('Görev arşivden geri yüklendi');
        // Reload işlemlerini ayrı try-catch içinde yap
        try {
          await Promise.all([loadTasks(), loadArchivedTasks()]);
        } catch (e) {
          console.log('Liste yenileme hatası:', e);
        }
      }
    } catch (error) {
      console.error('Geri yükleme hatası:', error);
      toast.error('Görev geri yüklenemedi');
    }
  };

  // useEffect'e bildirim yüklemesini ekle
  useEffect(() => {
    loadInternalTaskNotifications();
    loadGorevlerNotifications();
    // Her 30 saniyede bir güncelle
    const interval = setInterval(() => {
      loadInternalTaskNotifications();
      loadGorevlerNotifications();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Şube İçi Görevler sıralama fonksiyonu
  const handleInternalSort = (column) => {
    if (internalSortColumn === column) {
      setInternalSortDirection(internalSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setInternalSortColumn(column);
      setInternalSortDirection('asc');
    }
  };

  // Sıralanmış Şube İçi Görevler
  const sortedInternalTasks = [...internalTasks].sort((a, b) => {
    let aVal = a[internalSortColumn] || '';
    let bVal = b[internalSortColumn] || '';
    
    // Özel alanlar için değer çıkarımı
    if (internalSortColumn === 'created_by_name') {
      aVal = a.created_by_name || a.owner_name || '';
      bVal = b.created_by_name || b.owner_name || '';
    }
    
    // Tarih alanları için
    if (internalSortColumn === 'created_at' || internalSortColumn === 'updated_at' || internalSortColumn === 'due_date') {
      aVal = aVal || '';
      bVal = bVal || '';
    }
    
    // String karşılaştırma
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      const result = aVal.localeCompare(bVal, 'tr');
      return internalSortDirection === 'asc' ? result : -result;
    }
    
    // Sayısal karşılaştırma
    if (aVal < bVal) return internalSortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return internalSortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Sıralama ikonu bileşeni
  const InternalSortIcon = ({ column }) => {
    if (internalSortColumn !== column) {
      return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    }
    return internalSortDirection === 'asc' 
      ? <ArrowUp className="h-4 w-4 ml-1" />
      : <ArrowDown className="h-4 w-4 ml-1" />;
  };

  // Çalışan seçimini toggle et
  const toggleEmployee = (empId) => {
    setFormData(prev => ({
      ...prev,
      assigned_to_ids: prev.assigned_to_ids.includes(empId)
        ? prev.assigned_to_ids.filter(id => id !== empId)
        : [...prev.assigned_to_ids, empId]
    }));
  };

  const handleCreate = async () => {
    if (!formData.title) {
      toast.error('Başlık zorunludur');
      return;
    }
    if (formData.assigned_to_ids.length === 0) {
      toast.error('En az bir kişi seçmelisiniz');
      return;
    }
    
    setSubmitting(true);
    try {
      // Her seçili kişi için ayrı görev oluştur
      for (const empId of formData.assigned_to_ids) {
        await axios.post(`${API}/branch/tasks`, {
          title: formData.title,
          description: formData.description,
          assigned_to: empId,
          task_type: mainTab,
          priority: formData.priority,
          due_date: formData.due_date,
          documents: formData.documents
        }, { headers: getAuthHeaders() });
      }
      
      toast.success(`${formData.assigned_to_ids.length} kişiye görev atandı`);
      setCreateModalOpen(false);
      setFormData({ 
        title: '', 
        description: '', 
        assigned_to_ids: [], 
        task_type: 'daily', 
        priority: 'normal', 
        due_date: new Date().toISOString().split('T')[0],
        documents: []
      });
      loadData();
    } catch (error) {
      toast.error('Görev oluşturulamadı');
    }
    setSubmitting(false);
  };

  // Görev güncelle
  const handleUpdateTask = async () => {
    if (!selectedTask) return;
    
    setSubmitting(true);
    try {
      await axios.put(`${API}/branch/tasks/${selectedTask.id}`, {
        title: selectedTask.title,
        description: selectedTask.description,
        priority: selectedTask.priority,
        due_date: selectedTask.due_date,
        assigned_to_ids: selectedTask.send_to_users || []
      }, { headers: getAuthHeaders() });
      toast.success('Görev güncellendi');
      setEditModalOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Görev güncellenemedi');
    }
    setSubmitting(false);
  };

  // Düzenleme modalında kişi seçimi toggle
  const toggleEditUserSelection = (userId) => {
    if (!selectedTask) return;
    const currentUsers = selectedTask.send_to_users || [];
    const newUsers = currentUsers.includes(userId)
      ? currentUsers.filter(id => id !== userId)
      : [...currentUsers, userId];
    setSelectedTask({ ...selectedTask, send_to_users: newUsers });
  };

  // İlerleme kayıtlarını yükle
  const loadProgressLogs = async (taskId) => {
    try {
      const res = await axios.get(`${API}/work/gorevler/${taskId}/progress`, { headers: getAuthHeaders() });
      setProgressLogs(res.data.progress_logs || []);
    } catch (error) {
      console.error('Güncel durum yüklenemedi:', error);
      setProgressLogs([]);
    }
  };

  // Güncel durum modalını aç
  const handleOpenProgressModal = (task) => {
    setSelectedTask(task);
    loadProgressLogs(task.id);
    setNewProgressLog({ description: '', documents: [] });
    setProgressModalOpen(true);
  };

  // Belge yükleme
  const handleProgressDocUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        setNewProgressLog(prev => ({
          ...prev,
          documents: [...prev.documents, {
            name: file.name,
            type: file.type,
            size: file.size,
            data: reader.result
          }]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  // Yeni güncelleme ekle
  const handleAddProgressLog = async () => {
    if (!newProgressLog.description.trim()) {
      toast.error('Açıklama gerekli');
      return;
    }
    try {
      await axios.post(`${API}/work/gorevler/${selectedTask.id}/progress`, newProgressLog, { headers: getAuthHeaders() });
      toast.success('Güncel durum eklendi');
      setNewProgressLog({ description: '', documents: [] });
      loadProgressLogs(selectedTask.id);
      loadData();
    } catch (error) {
      toast.error('Güncel durum eklenemedi');
    }
  };

  // Görevi Bitir bildirimi modalını aç (başkasının görevi için)
  const handleOpenFinishNotifyModal = (task) => {
    setSelectedTask(task);
    setFinishNotifyNotes('');
    setDetailModalOpen(false);
    setFinishNotifyModalOpen(true);
  };

  // Sıralama fonksiyonu
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sıralama ikonu
  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-amber-600" />
      : <ArrowDown className="h-4 w-4 text-amber-600" />;
  };

  // Sıralanmış görevler
  const sortedTasks = React.useMemo(() => {
    if (!sortConfig.key) return tasks;
    
    return [...tasks].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortConfig.key) {
        case 'title':
          aValue = a.title?.toLowerCase() || '';
          bValue = b.title?.toLowerCase() || '';
          break;
        case 'created_by_name':
          aValue = (a.created_by_name || '').toLowerCase();
          bValue = (b.created_by_name || '').toLowerCase();
          break;
        case 'created_at':
          aValue = new Date(a.created_at || 0).getTime();
          bValue = new Date(b.created_at || 0).getTime();
          break;
        case 'accepted_by':
          aValue = a.accepted_by?.length || 0;
          bValue = b.accepted_by?.length || 0;
          break;
        case 'due_date':
          aValue = new Date(a.start_date || a.due_date || 0).getTime();
          bValue = new Date(b.start_date || b.due_date || 0).getTime();
          break;
        case 'priority':
          const priorityOrder = { high: 3, normal: 2, low: 1 };
          aValue = priorityOrder[a.priority] || 0;
          bValue = priorityOrder[b.priority] || 0;
          break;
        case 'status':
          const statusOrder = { completed: 4, in_progress: 3, accepted: 2, pending: 1 };
          aValue = statusOrder[a.status] || 0;
          bValue = statusOrder[b.status] || 0;
          break;
        case 'completion_date':
          aValue = a.completion_date ? new Date(a.completion_date).getTime() : 0;
          bValue = b.completion_date ? new Date(b.completion_date).getTime() : 0;
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [tasks, sortConfig]);

  // Çoklu seçim fonksiyonları
  const toggleTaskSelection = (taskId) => {
    setSelectedTasks(prev => 
      prev.includes(taskId) 
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  const selectAllTasks = () => {
    const taskIds = sortedTasks.map(t => t.id);
    const allSelected = taskIds.every(id => selectedTasks.includes(id));
    if (allSelected) {
      setSelectedTasks([]);
    } else {
      setSelectedTasks(taskIds);
    }
  };

  const handleDeleteSelectedTasks = async () => {
    if (selectedTasks.length === 0) return;
    
    setDeleting(true);
    try {
      let successCount = 0;
      let failCount = 0;
      
      for (const taskId of selectedTasks) {
        try {
          // Önce task'ı bul - source'a göre endpoint belirle
          const task = tasks.find(t => t.id === taskId);
          
          // work_gorevler endpoint'ini dene (her ikisini de siler)
          try {
            await axios.delete(`${API}/work/gorevler/${taskId}`, { headers: getAuthHeaders() });
            successCount++;
          } catch (workError) {
            // Eğer work_gorevler'de yoksa branch_tasks endpoint'ini dene
            if (workError.response?.status === 404) {
              await axios.delete(`${API}/branch/tasks/${taskId}`, { headers: getAuthHeaders() });
              successCount++;
            } else {
              throw workError;
            }
          }
        } catch (error) {
          console.error(`Görev silinemedi (${taskId}):`, error);
          failCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`${successCount} görev silindi`);
        loadData();
      }
      if (failCount > 0) {
        toast.error(`${failCount} görev silinemedi`);
      }
      
      setSelectedTasks([]);
      setShowDeleteModal(false);
    } catch (error) {
      toast.error('Silme işlemi sırasında hata oluştu');
    } finally {
      setDeleting(false);
    }
  };

  // Görevi bitirdiğini bildir (durumu değiştirmez)
  const handleFinishNotify = async () => {
    if (!finishNotifyNotes.trim()) {
      toast.error('Bitirme açıklaması gerekli');
      return;
    }
    
    try {
      await axios.post(`${API}/work/gorevler/${selectedTask.id}/finish-notify`, {
        notes: finishNotifyNotes
      }, { headers: getAuthHeaders() });
      toast.success('Görev bitirme bildirimi gönderildi');
      setFinishNotifyModalOpen(false);
      loadData();
    } catch (error) {
      toast.error('Bildirim gönderilemedi');
    }
  };

  // Görevi tamamla modalını aç
  const handleOpenCompleteModal = (task) => {
    setSelectedTask(task);
    setCompletionNotes('');
    setCompleteModalOpen(true);
  };

  // Görevi tamamla
  const handleCompleteTask = async () => {
    if (!completionNotes.trim()) {
      toast.error('Tamamlama açıklaması gerekli');
      return;
    }
    try {
      const completionDateTime = new Date().toISOString();
      
      await axios.put(`${API}/work/gorevler/${selectedTask.id}`, {
        status: 'completed',
        completion_notes: completionNotes,
        completion_date: completionDateTime,
        completed_by: user?.id,
        completed_by_name: user?.name
      }, { headers: getAuthHeaders() });
      
      toast.success('Görev tamamlandı');
      setCompleteModalOpen(false);
      setDetailModalOpen(false);
      loadData();
    } catch (error) {
      toast.error('Görev tamamlanamadı');
    }
  };

  // Belge indir
  const downloadDocument = (doc) => {
    const link = document.createElement('a');
    link.href = doc.data;
    link.download = doc.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: { label: 'Bekliyor', class: 'bg-yellow-100 text-yellow-700' },
      accepted: { label: 'Başlatıldı', class: 'bg-purple-100 text-purple-700' },
      in_progress: { label: 'Devam Ediyor', class: 'bg-blue-100 text-blue-700' },
      completed: { label: 'Tamamlandı', class: 'bg-green-100 text-green-700' },
      cancelled: { label: 'İptal', class: 'bg-gray-100 text-gray-700' }
    };
    const v = variants[status] || variants.pending;
    return <Badge className={v.class}>{v.label}</Badge>;
  };

  // Görevi başlat fonksiyonu
  const handleStartTask = async (task) => {
    try {
      // İş Paneli görevleri için
      if (task.source === 'work_gorevler' || !task.created_by_branch) {
        await axios.put(`${API}/work/gorevler/${task.id}/start`, {}, { headers: getAuthHeaders() });
      } else {
        // Şube görevleri için
        await axios.put(`${API}/branch/tasks/${task.id}/start`, {}, { headers: getAuthHeaders() });
      }
      toast.success('Görev başlatıldı');
      setDetailModalOpen(false);
      loadData();
    } catch (error) {
      console.error('Görev başlatma hatası:', error);
      toast.error('Görev başlatılamadı');
    }
  };

  const getPriorityBadge = (priority) => {
    const variants = {
      low: { label: 'Düşük', class: 'bg-gray-100 text-gray-600' },
      normal: { label: 'Normal', class: 'bg-blue-100 text-blue-600' },
      high: { label: 'Yüksek', class: 'bg-orange-100 text-orange-600' },
      urgent: { label: 'Acil', class: 'bg-red-100 text-red-600' }
    };
    const v = variants[priority] || variants.normal;
    return <Badge className={v.class}>{v.label}</Badge>;
  };

  const getSourceBadge = (task) => {
    if (task.created_by_branch) {
      return <Badge className="bg-amber-100 text-amber-700">Şube</Badge>;
    }
    return <Badge className="bg-blue-100 text-blue-700">İş Paneli</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="branch-tasks">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Görevler</h1>
          <p className="text-gray-500">Şube görevlerini yönetin (İş Paneli görevleri dahil)</p>
        </div>
      </div>

      <Tabs value={mainTab} onValueChange={setMainTab}>
        <TabsList className="grid w-full grid-cols-4 mb-4">
          <TabsTrigger value="daily" data-testid="tab-daily">
            <Calendar className="h-4 w-4 mr-2" />
            GÜNLÜK GÖREV
          </TabsTrigger>
          <TabsTrigger value="internal" data-testid="tab-internal" className="relative">
            <Users className="h-4 w-4 mr-2" />
            ŞUBE İÇİ GÖREVLER ({internalTasks.length})
            {unreadInternalTaskIds.size > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {unreadInternalTaskIds.size}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="monthly" data-testid="tab-monthly">
            <Clock className="h-4 w-4 mr-2" />
            AYLIK GÖREV
          </TabsTrigger>
          <TabsTrigger value="archive" data-testid="tab-archive" className="relative">
            <Archive className="h-4 w-4 mr-2" />
            ARŞİV ({archivedTasks.length})
          </TabsTrigger>
        </TabsList>

        {/* Şube İçi Görevler Tab */}
        <TabsContent value="internal">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-700">
                <Users className="h-5 w-5" />
                Şube İçi Görevler - Güncel Durumlar
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
                </div>
              ) : internalTasks.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="h-16 w-16 mx-auto mb-4 text-blue-200" />
                  <h3 className="text-xl font-bold mb-2 text-gray-600">Şube İçi Görev Yok</h3>
                  <p className="text-gray-500">Şube yöneticileri arasında henüz görevlendirme yapılmamış</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead 
                        className="font-bold cursor-pointer hover:bg-gray-100"
                        onClick={() => handleInternalSort('title')}
                      >
                        <div className="flex items-center">
                          Görev
                          <InternalSortIcon column="title" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-bold cursor-pointer hover:bg-gray-100"
                        onClick={() => handleInternalSort('created_by_name')}
                      >
                        <div className="flex items-center">
                          Oluşturan
                          <InternalSortIcon column="created_by_name" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-bold cursor-pointer hover:bg-gray-100"
                        onClick={() => handleInternalSort('assigned_to_name')}
                      >
                        <div className="flex items-center">
                          Atanan
                          <InternalSortIcon column="assigned_to_name" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-bold cursor-pointer hover:bg-gray-100"
                        onClick={() => handleInternalSort('priority')}
                      >
                        <div className="flex items-center">
                          Öncelik
                          <InternalSortIcon column="priority" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-bold cursor-pointer hover:bg-gray-100"
                        onClick={() => handleInternalSort('due_date')}
                      >
                        <div className="flex items-center">
                          Tarih
                          <InternalSortIcon column="due_date" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-bold cursor-pointer hover:bg-gray-100"
                        onClick={() => handleInternalSort('status')}
                      >
                        <div className="flex items-center">
                          Durum
                          <InternalSortIcon column="status" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-bold cursor-pointer hover:bg-gray-100"
                        onClick={() => handleInternalSort('updated_at')}
                      >
                        <div className="flex items-center">
                          Son Güncelleme
                          <InternalSortIcon column="updated_at" />
                        </div>
                      </TableHead>
                      <TableHead className="font-bold">İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedInternalTasks.map(task => {
                      const statusConfig = {
                        pending: { label: 'Bekliyor', class: 'bg-amber-100 text-amber-700' },
                        in_progress: { label: 'Devam Ediyor', class: 'bg-blue-100 text-blue-700' },
                        completed: { label: 'Tamamlandı', class: 'bg-green-100 text-green-700' },
                        submitted: { label: 'Onay Bekliyor', class: 'bg-purple-100 text-purple-700' },
                        approved: { label: 'Onaylandı', class: 'bg-emerald-100 text-emerald-700' },
                        rejected: { label: 'Reddedildi', class: 'bg-red-100 text-red-700' },
                        cancelled: { label: 'İptal', class: 'bg-gray-100 text-gray-700' }
                      };
                      const priorityConfig = {
                        low: { label: 'Düşük', class: 'bg-gray-100 text-gray-600' },
                        normal: { label: 'Normal', class: 'bg-blue-50 text-blue-600' },
                        high: { label: 'Yüksek', class: 'bg-orange-100 text-orange-700' },
                        urgent: { label: 'Acil', class: 'bg-red-100 text-red-700' }
                      };
                      const s = statusConfig[task.status] || statusConfig.pending;
                      const p = priorityConfig[task.priority] || priorityConfig.normal;
                      const isUnread = unreadInternalTaskIds.has(task.id);
                      
                      return (
                        <TableRow 
                          key={task.id} 
                          className={`hover:bg-gray-50 cursor-pointer ${isUnread ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''}`}
                          onClick={() => {
                            if (isUnread) {
                              markInternalTaskAsRead(task.id);
                            }
                          }}
                        >
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium">{task.title}</p>
                                  {isUnread && (
                                    <Badge className="bg-red-500 text-white text-xs">Yeni</Badge>
                                  )}
                                </div>
                                {task.description && (
                                  <p className="text-sm text-gray-500 line-clamp-1">{task.description}</p>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4 text-gray-400" />
                              <span>{task.created_by_name || task.owner_name || '-'}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4 text-blue-400" />
                              <span>{task.assigned_to_name || '-'}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={p.class}>{p.label}</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              {task.due_date || task.start_date || '-'}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={s.class}>{s.label}</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm text-gray-500">
                              {task.updated_at 
                                ? new Date(task.updated_at).toLocaleDateString('tr-TR', { 
                                    day: '2-digit', month: '2-digit', year: 'numeric',
                                    hour: '2-digit', minute: '2-digit'
                                  })
                                : task.created_at 
                                  ? new Date(task.created_at).toLocaleDateString('tr-TR', { 
                                      day: '2-digit', month: '2-digit', year: 'numeric',
                                      hour: '2-digit', minute: '2-digit'
                                    })
                                  : '-'
                              }
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button 
                                variant="outline" 
                                size="sm" 
                                title="Görüntüle"
                                onClick={() => { setSelectedTask(task); setDetailModalOpen(true); }}
                                data-testid={`view-internal-task-${task.id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                title="Arşivle"
                                className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                                onClick={() => handleArchiveTask(task.id)}
                                data-testid={`archive-internal-task-${task.id}`}
                              >
                                <Archive className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value={mainTab === 'internal' ? '' : mainTab}>
        </TabsContent>

        {/* Arşiv Tab */}
        <TabsContent value="archive">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-gray-600">
                <Archive className="h-5 w-5" />
                Arşivlenmiş Görevler
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
                </div>
              ) : archivedTasks.length === 0 ? (
                <div className="text-center py-12">
                  <Archive className="h-16 w-16 mx-auto mb-4 text-gray-200" />
                  <h3 className="text-xl font-bold mb-2 text-gray-600">Arşivde Görev Yok</h3>
                  <p className="text-gray-500">Arşivlenmiş görev bulunmuyor</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-bold">Görev</TableHead>
                      <TableHead className="font-bold">Oluşturan</TableHead>
                      <TableHead className="font-bold">Atanan</TableHead>
                      <TableHead className="font-bold">Durum</TableHead>
                      <TableHead className="font-bold">Arşiv Tarihi</TableHead>
                      <TableHead className="font-bold">İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {archivedTasks.map(task => (
                      <TableRow key={task.id} className="hover:bg-gray-50">
                        <TableCell>
                          <div className="font-medium">{task.title}</div>
                          {task.description && (
                            <div className="text-sm text-gray-500 truncate max-w-[200px]">{task.description}</div>
                          )}
                        </TableCell>
                        <TableCell>{task.created_by_name || task.owner_name || '-'}</TableCell>
                        <TableCell>{task.assigned_to_name || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-gray-500">
                            {task.status || 'Bilinmiyor'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {task.archived_at ? new Date(task.archived_at).toLocaleDateString('tr-TR', {
                            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                          }) : '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              title="Görüntüle"
                              onClick={() => { setSelectedTask(task); setDetailModalOpen(true); }}
                              data-testid={`view-archived-task-${task.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              title="Geri Yükle"
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              onClick={() => handleRestoreTask(task.id)}
                              data-testid={`restore-task-${task.id}`}
                            >
                              <RotateCcw className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Daily and Monthly Tasks Content */}
        {(mainTab === 'daily' || mainTab === 'monthly') && (
          <div>
          <div className="flex justify-end mb-4">
            <Button onClick={() => setCreateModalOpen(true)} className="bg-amber-600 hover:bg-amber-700">
              <Plus className="h-4 w-4 mr-2" />
              GÖREV EKLE
            </Button>
          </div>

          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
                </div>
              ) : sortedTasks.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <ClipboardList className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Henüz görev bulunmuyor</p>
                </div>
              ) : (
                <>
                  {/* Seçim Toolbar */}
                  <div className="flex items-center justify-between p-3 border-b">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={sortedTasks.length > 0 && sortedTasks.every(t => selectedTasks.includes(t.id))}
                        onCheckedChange={() => selectAllTasks()}
                      />
                      <span className="text-sm text-gray-500">
                        {selectedTasks.filter(id => sortedTasks.some(t => t.id === id)).length > 0 
                          ? `${selectedTasks.filter(id => sortedTasks.some(t => t.id === id)).length} seçili` 
                          : 'Tümünü seç'}
                      </span>
                    </div>
                    {selectedTasks.length > 0 && (
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => setSelectedTasks([])}>
                          Seçimi Temizle
                        </Button>
                        <Button variant="destructive" size="sm" onClick={() => setShowDeleteModal(true)} className="gap-1">
                          <Trash2 className="h-4 w-4" />
                          Sil ({selectedTasks.length})
                        </Button>
                      </div>
                    )}
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10"></TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-gray-50"
                          onClick={() => handleSort('title')}
                        >
                          <div className="flex items-center gap-1">
                            Görev <SortIcon columnKey="title" />
                          </div>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-gray-50"
                          onClick={() => handleSort('created_by_name')}
                        >
                          <div className="flex items-center gap-1">
                            Oluşturan <SortIcon columnKey="created_by_name" />
                          </div>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-gray-50"
                          onClick={() => handleSort('created_at')}
                        >
                          <div className="flex items-center gap-1">
                            Oluşturulma <SortIcon columnKey="created_at" />
                          </div>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-gray-50"
                          onClick={() => handleSort('accepted_by')}
                        >
                          <div className="flex items-center gap-1">
                            Kabul Edenler <SortIcon columnKey="accepted_by" />
                          </div>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-gray-50"
                          onClick={() => handleSort('due_date')}
                        >
                          <div className="flex items-center gap-1">
                            Tarih Aralığı <SortIcon columnKey="due_date" />
                          </div>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-gray-50"
                          onClick={() => handleSort('priority')}
                        >
                          <div className="flex items-center gap-1">
                            Öncelik <SortIcon columnKey="priority" />
                          </div>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-gray-50"
                          onClick={() => handleSort('status')}
                        >
                          <div className="flex items-center gap-1">
                            Durum <SortIcon columnKey="status" />
                          </div>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-gray-50"
                          onClick={() => handleSort('updated_at')}
                        >
                          <div className="flex items-center gap-1">
                            Son Güncelleme <SortIcon columnKey="updated_at" />
                          </div>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-gray-50"
                          onClick={() => handleSort('completion_date')}
                        >
                          <div className="flex items-center gap-1">
                            Tamamlanma Zamanı <SortIcon columnKey="completion_date" />
                          </div>
                        </TableHead>
                        <TableHead>İşlemler</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedTasks.map(task => {
                        const isUnread = unreadGorevlerIds.has(task.id);
                        return (
                        <TableRow 
                          key={task.id}
                          className={`${selectedTasks.includes(task.id) ? 'bg-amber-50' : ''} ${isUnread ? 'bg-blue-50 ring-1 ring-blue-200' : ''}`}
                          onClick={() => {
                            if (isUnread) {
                              markGorevAsRead(task.id);
                            }
                          }}
                        >
                          <TableCell>
                            <Checkbox
                              checked={selectedTasks.includes(task.id)}
                              onCheckedChange={() => toggleTaskSelection(task.id)}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div>
                                <p className="font-medium">{task.title}</p>
                                {task.description && (
                                  <p className="text-sm text-gray-500 truncate max-w-[200px]">{task.description}</p>
                                )}
                              </div>
                              {isUnread && (
                                <Badge className="bg-blue-500 text-white text-xs animate-pulse">YENİ</Badge>
                              )}
                            </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-gray-400" />
                            {task.created_by_name || 'Bilinmiyor'}
                          </div>
                        </TableCell>
                        <TableCell>
                          {task.created_at ? new Date(task.created_at).toLocaleDateString('tr-TR', {
                            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                          }) : '-'}
                        </TableCell>
                        <TableCell>
                          {task.accepted_by && task.accepted_by.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {task.accepted_by.map((a, idx) => (
                                <Badge key={idx} className="bg-green-100 text-green-700 text-xs">
                                  {a.user_name || employees.find(e => e.id === a.user_id)?.name || 'Bilinmiyor'}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {(task.start_date || task.due_date) ? (
                            <span className="text-sm">
                              {task.start_date || task.due_date}
                              {task.end_date && ` - ${task.end_date}`}
                            </span>
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell>{getPriorityBadge(task.priority)}</TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            {getStatusBadge(task.status)}
                            {task.finish_notifications && task.finish_notifications.length > 0 && task.status !== 'completed' && (
                              <Badge className="bg-amber-100 text-amber-700 gap-1 text-xs">
                                <Send className="h-3 w-3" />
                                Görev Bitmiş
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {task.updated_at ? (
                            <div className="text-sm text-blue-600 font-medium">
                              {new Date(task.updated_at).toLocaleDateString('tr-TR', {
                                day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                              })}
                            </div>
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {task.status === 'completed' && task.completion_date ? (
                            <div className="text-sm text-green-600">
                              {new Date(task.completion_date).toLocaleDateString('tr-TR', {
                                day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                              })}
                            </div>
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              title="Görüntüle"
                              onClick={() => { setSelectedTask(task); setDetailModalOpen(true); }}
                              data-testid={`view-task-${task.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              title="Düzenle"
                              className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                              onClick={() => { setSelectedTask(task); setEditModalOpen(true); }}
                              data-testid={`edit-task-${task.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              title="Arşivle"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleArchiveTask(task.id)}
                              data-testid={`archive-task-${task.id}`}
                            >
                              <Archive className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )})}
                  </TableBody>
                </Table>
                </>
              )}
            </CardContent>
          </Card>
        </div>
        )}
      </Tabs>

      {/* Create Modal - Çoklu Seçim */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-amber-600" />
              Yeni Görev Ekle
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Başlık *</Label>
              <Input 
                value={formData.title} 
                onChange={e => setFormData({...formData, title: e.target.value})} 
                placeholder="Görev başlığı" 
              />
            </div>
            <div className="space-y-2">
              <Label>Açıklama</Label>
              <Textarea 
                value={formData.description} 
                onChange={e => setFormData({...formData, description: e.target.value})} 
                placeholder="Görev açıklaması" 
                rows={3} 
              />
            </div>
            
            {/* Çoklu Kişi Seçimi */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Atanacak Kişiler * ({formData.assigned_to_ids.length} seçili)
              </Label>
              <div className="border rounded-lg divide-y max-h-[200px] overflow-y-auto">
                {employees.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">Çalışan bulunamadı</div>
                ) : (
                  employees.map(emp => {
                    const isSelected = formData.assigned_to_ids.includes(emp.id);
                    return (
                      <div 
                        key={emp.id} 
                        className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50 ${isSelected ? 'bg-amber-50' : ''}`}
                        onClick={() => toggleEmployee(emp.id)}
                      >
                        <Checkbox checked={isSelected} onCheckedChange={() => toggleEmployee(emp.id)} />
                        <div>
                          <p className="font-medium">{emp.name || 'İsimsiz'}</p>
                          <p className="text-sm text-gray-500">{emp.position_name || emp.profession || '-'}</p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Öncelik</Label>
                <Select value={formData.priority} onValueChange={v => setFormData({...formData, priority: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Düşük</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">Yüksek</SelectItem>
                    <SelectItem value="urgent">Acil</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Tarih</Label>
                <Input 
                  type="date" 
                  value={formData.due_date} 
                  onChange={e => setFormData({...formData, due_date: e.target.value})} 
                />
              </div>
            </div>

            {/* Belge Yükleme */}
            <div className="border rounded-lg p-3 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <Upload className="h-4 w-4 text-blue-500" />
                  Belgeler
                </Label>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleTaskFileUpload}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                    data-testid="task-file-input"
                  />
                  <span className="text-sm text-blue-600 hover:underline">+ Belge Ekle</span>
                </label>
              </div>
              
              {formData.documents.length > 0 && (
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {formData.documents.map((doc, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-gray-50 p-2 rounded text-sm">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <span className="truncate">{doc.name}</span>
                        <span className="text-xs text-gray-400">({formatFileSize(doc.size)})</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-red-400 hover:text-red-600"
                        onClick={() => removeTaskDocument(idx)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModalOpen(false)}>İptal</Button>
            <Button onClick={handleCreate} disabled={submitting} className="bg-amber-600 hover:bg-amber-700">
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {formData.assigned_to_ids.length > 1 
                ? `${formData.assigned_to_ids.length} Kişiye Ata` 
                : 'Oluştur'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Görev Detay Modalı */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-amber-600" />
              Görev Detayı
            </DialogTitle>
          </DialogHeader>
          
          {selectedTask && (
            <div className="space-y-4 py-4">
              {/* Görev Başlık ve Durum */}
              <div className="bg-amber-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-amber-100 text-amber-700">
                    {mainTab === 'daily' ? 'Günlük Görev' : 'Aylık Görev'}
                  </Badge>
                  {getStatusBadge(selectedTask.status)}
                  {getPriorityBadge(selectedTask.priority)}
                </div>
                <h3 className="font-bold text-lg text-gray-900">{selectedTask.title}</h3>
                {selectedTask.description && (
                  <p className="text-gray-600 mt-2">{selectedTask.description}</p>
                )}
              </div>
              
              {/* Detay Bilgileri */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-gray-500 text-sm">Oluşturan</Label>
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-gray-400" />
                    <span className="font-medium">{selectedTask.created_by_name || 'Bilinmiyor'}</span>
                  </div>
                </div>
                
                <div className="space-y-1">
                  <Label className="text-gray-500 text-sm">Oluşturulma Tarihi</Label>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span className="font-medium">
                      {selectedTask.created_at ? new Date(selectedTask.created_at).toLocaleDateString('tr-TR', {
                        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                      }) : '-'}
                    </span>
                  </div>
                </div>
                
                <div className="space-y-1">
                  <Label className="text-gray-500 text-sm">Atanan Kişi</Label>
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-gray-400" />
                    <span className="font-medium">{selectedTask.assigned_to_name || '-'}</span>
                  </div>
                </div>
                
                <div className="space-y-1">
                  <Label className="text-gray-500 text-sm">Tarih Aralığı</Label>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span className="font-medium">
                      {selectedTask.start_date || selectedTask.due_date || '-'}
                      {selectedTask.end_date && ` - ${selectedTask.end_date}`}
                    </span>
                  </div>
                </div>
              </div>

              {/* Kabul Edenler */}
              {selectedTask.accepted_by && selectedTask.accepted_by.length > 0 && (
                <div className="pt-4 border-t">
                  <Label className="text-gray-500 text-sm mb-2 block">Kabul Edenler</Label>
                  <div className="flex flex-wrap gap-2">
                    {selectedTask.accepted_by.map((a, idx) => (
                      <Badge key={idx} className="bg-green-100 text-green-700">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        {a.user_name || employees.find(e => e.id === a.user_id)?.name || 'Bilinmiyor'}
                        <span className="text-xs ml-1 opacity-70">
                          ({new Date(a.accepted_at).toLocaleDateString('tr-TR')})
                        </span>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Bitirme Bildirimleri */}
              {selectedTask.finish_notifications && selectedTask.finish_notifications.length > 0 && (
                <div className="pt-4 border-t">
                  <Label className="text-gray-500 text-sm mb-2 block flex items-center gap-2">
                    <Send className="h-4 w-4 text-amber-600" />
                    Bitirme Bildirimleri
                  </Label>
                  <div className="space-y-2">
                    {selectedTask.finish_notifications.map((notification, idx) => (
                      <div key={idx} className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-amber-800">{notification.user_name}</span>
                          <span className="text-xs text-amber-600">
                            {new Date(notification.notified_at).toLocaleDateString('tr-TR', {
                              day: '2-digit', month: '2-digit', year: 'numeric',
                              hour: '2-digit', minute: '2-digit'
                            })}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700">{notification.notes}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tamamlanma Bilgisi */}
              {selectedTask.status === 'completed' && (
                <div className="pt-4 border-t">
                  <Label className="text-gray-500 text-sm mb-2 block">Tamamlanma Bilgisi</Label>
                  <div className="bg-green-50 p-3 rounded-lg">
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle className="h-4 w-4" />
                      <span className="font-medium">
                        {selectedTask.completion_date 
                          ? new Date(selectedTask.completion_date).toLocaleDateString('tr-TR', {
                              day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                            })
                          : 'Tamamlandı'}
                      </span>
                    </div>
                    {selectedTask.completed_by_name && (
                      <p className="text-sm text-green-600 mt-1">
                        Tamamlayan: {selectedTask.completed_by_name}
                      </p>
                    )}
                    {selectedTask.completion_notes && (
                      <p className="text-sm text-gray-600 mt-2 bg-white p-2 rounded">
                        {selectedTask.completion_notes}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Ekli Belgeler (görev üzerinde varsa) */}
              {selectedTask.documents && selectedTask.documents.length > 0 && (
                <div className="pt-4 border-t">
                  <Label className="text-gray-500 text-sm mb-2 block flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Ekli Belgeler ({selectedTask.documents.length})
                  </Label>
                  <div className="space-y-2">
                    {selectedTask.documents.map((doc, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-gray-500" />
                          <span className="text-sm">{doc.name}</span>
                          <span className="text-xs text-gray-400">{formatFileSize(doc.size)}</span>
                        </div>
                        <div className="flex gap-1">
                          {isPreviewable(doc.type) && (
                            <Button variant="ghost" size="sm" onClick={() => setPreviewDoc(doc)}>
                              <Eye className="h-4 w-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => downloadDocument(doc)}>
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* İlerleme Kayıtları Özeti */}
              {selectedTask.progress_logs && selectedTask.progress_logs.length > 0 && (
                <div className="pt-4 border-t">
                  <Label className="text-gray-500 text-sm mb-2 block flex items-center gap-2">
                    <Edit className="h-4 w-4 text-blue-600" />
                    Son Güncellemeler ({selectedTask.progress_logs.length})
                  </Label>
                  <div className="space-y-2 max-h-[150px] overflow-y-auto">
                    {selectedTask.progress_logs.slice(-3).map((log, idx) => (
                      <div key={idx} className="bg-blue-50 p-2 rounded text-sm">
                        <div className="flex justify-between text-xs text-gray-500 mb-1">
                          <span>{log.user_name}</span>
                          <span>{new Date(log.created_at).toLocaleDateString('tr-TR')}</span>
                        </div>
                        <p className="text-gray-700 line-clamp-2">{log.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setDetailModalOpen(false)}>
              Kapat
            </Button>
            
            {/* Görevi Başlat butonu - sadece bekleyen görevler için ve atanan kişi */}
            {selectedTask && 
             selectedTask.status === 'pending' && 
             (selectedTask.assigned_to_user_id === user?.id || 
              selectedTask.assigned_to === user?.id ||
              (selectedTask.send_to_users && selectedTask.send_to_users.includes(user?.id))) && (
              <Button 
                onClick={() => handleStartTask(selectedTask)}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="start-task-btn"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Görevi Başlat
              </Button>
            )}
            
            {/* Güncel Durum butonu - tüm aktif görevler için */}
            {selectedTask && selectedTask.status !== 'completed' && selectedTask.status !== 'cancelled' && (
              <Button 
                onClick={() => { setDetailModalOpen(false); handleOpenProgressModal(selectedTask); }}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Edit className="h-4 w-4 mr-2" />
                Güncel Durum
              </Button>
            )}
            
            {/* Görevi Bitir butonu - SADECE atanan kişi için (bildirim gönderir, tamamlamaz) */}
            {selectedTask && 
             selectedTask.status !== 'completed' && 
             selectedTask.status !== 'cancelled' && 
             (selectedTask.created_by || selectedTask.owner_id) !== user?.id &&
             (selectedTask.assigned_to_user_id === user?.id || 
              selectedTask.assigned_to === user?.id ||
              (selectedTask.send_to_users && selectedTask.send_to_users.includes(user?.id))) && (
              <Button 
                onClick={() => handleOpenFinishNotifyModal(selectedTask)}
                className="bg-amber-600 hover:bg-amber-700"
              >
                <Send className="h-4 w-4 mr-2" />
                Görevi Bitir
              </Button>
            )}
            
            {/* Tamamla butonu - sadece kendi oluşturduğu görevler için */}
            {selectedTask && 
             selectedTask.status !== 'completed' && 
             selectedTask.status !== 'cancelled' && 
             ((selectedTask.created_by && selectedTask.created_by === user?.id) || 
              (selectedTask.owner_id && selectedTask.owner_id === user?.id)) && (
              <Button 
                onClick={() => { setDetailModalOpen(false); handleOpenCompleteModal(selectedTask); }}
                className="bg-green-600 hover:bg-green-700"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Tamamla
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Düzenleme Modalı */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5 text-blue-500" />
              Görev Düzenle
            </DialogTitle>
          </DialogHeader>
          
          {selectedTask && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Başlık</Label>
                <Input 
                  value={selectedTask.title || ''} 
                  onChange={e => setSelectedTask({...selectedTask, title: e.target.value})}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Açıklama</Label>
                <Textarea 
                  value={selectedTask.description || ''} 
                  onChange={e => setSelectedTask({...selectedTask, description: e.target.value})}
                  rows={3}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Öncelik</Label>
                  <Select 
                    value={selectedTask.priority || 'normal'} 
                    onValueChange={v => setSelectedTask({...selectedTask, priority: v})}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Düşük</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="high">Yüksek</SelectItem>
                      <SelectItem value="urgent">Acil</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Tarih</Label>
                  <Input 
                    type="date" 
                    value={selectedTask.due_date || selectedTask.start_date || ''} 
                    onChange={e => setSelectedTask({...selectedTask, due_date: e.target.value})} 
                  />
                </div>
              </div>
              
              {/* Kişilere Ata */}
              <div className="border rounded-lg p-3 space-y-3">
                <Label className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-green-500" />
                  Atanan Kişiler ({(selectedTask.send_to_users || []).length} seçili)
                </Label>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {employees.map(emp => {
                    const isSelected = (selectedTask.send_to_users || []).includes(emp.id);
                    return (
                      <div
                        key={emp.id}
                        className={`flex items-center justify-between p-2 rounded cursor-pointer ${
                          isSelected ? 'bg-green-100' : 'hover:bg-gray-100'
                        }`}
                        onClick={() => toggleEditUserSelection(emp.id)}
                      >
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-gray-400" />
                          <span className="text-sm">{emp.name}</span>
                        </div>
                        {isSelected && (
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        )}
                      </div>
                    );
                  })}
                  {employees.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-2">Çalışan bulunamadı</p>
                  )}
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleUpdateTask}
              disabled={submitting}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Güncelle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Güncel Durum Modalı */}
      <Dialog open={progressModalOpen} onOpenChange={setProgressModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-500" />
              Güncel Durum - {selectedTask?.title}
            </DialogTitle>
          </DialogHeader>
          
          {/* Yeni Güncelleme Ekle */}
          {selectedTask?.status !== 'completed' && selectedTask?.status !== 'cancelled' && (
            <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <Plus className="h-5 w-5 text-green-600" />
                <Label className="font-semibold">Yeni Güncelleme Ekle</Label>
              </div>
              <Textarea
                value={newProgressLog.description}
                onChange={(e) => setNewProgressLog(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Ne yapıldı? Açıklama yazın..."
                rows={3}
              />
              <div className="flex items-center justify-between">
                <label className="cursor-pointer flex items-center gap-2 text-sm text-blue-600 hover:underline">
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleProgressDocUpload}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                  />
                  <Upload className="h-4 w-4" />
                  Belge/Fotoğraf Ekle
                </label>
                <Button onClick={handleAddProgressLog} className="bg-green-600 hover:bg-green-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Ekle
                </Button>
              </div>
              {newProgressLog.documents.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {newProgressLog.documents.map((doc, idx) => (
                    <Badge key={idx} variant="outline" className="gap-1">
                      <FileText className="h-3 w-3" />
                      {doc.name}
                      <button
                        onClick={() => setNewProgressLog(prev => ({
                          ...prev,
                          documents: prev.documents.filter((_, i) => i !== idx)
                        }))}
                        className="ml-1 text-red-500 hover:text-red-700"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Tamamlanmış/İptal edilmiş için bilgi mesajı */}
          {(selectedTask?.status === 'completed' || selectedTask?.status === 'cancelled') && (
            <div className="border rounded-lg p-4 bg-blue-50 text-blue-700">
              <p className="text-sm flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Bu görev {selectedTask?.status === 'completed' ? 'tamamlanmış' : 'iptal edilmiş'}. Sadece geçmiş kayıtları görüntüleyebilirsiniz.
              </p>
            </div>
          )}

          {/* Güncellemeler Listesi */}
          <div className="space-y-4 mt-4">
            <Label className="font-semibold">Güncelleme Geçmişi</Label>
            {progressLogs.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>Henüz güncelleme yapılmamış</p>
              </div>
            ) : (
              <div className="space-y-3">
                {progressLogs.map((log) => (
                  <div key={log.id} className="border rounded-lg p-4 bg-white">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <span className="font-medium">{log.user_name}</span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {new Date(log.created_at).toLocaleDateString('tr-TR', {
                          day: '2-digit', month: '2-digit', year: 'numeric',
                          hour: '2-digit', minute: '2-digit'
                        })}
                      </span>
                    </div>
                    <p className="text-gray-700 mb-2">{log.description}</p>
                    {log.documents && log.documents.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t">
                        {log.documents.map((doc, idx) => (
                          <div key={idx} className="flex items-center gap-1 bg-gray-100 rounded px-2 py-1">
                            <FileText className="h-3 w-3 text-gray-500" />
                            <span className="text-xs">{doc.name}</span>
                            {isPreviewable(doc.type) && (
                              <button
                                onClick={() => setPreviewDoc(doc)}
                                className="text-blue-600 hover:text-blue-800 ml-1"
                                title="Önizle"
                              >
                                <Eye className="h-3 w-3" />
                              </button>
                            )}
                            <button
                              onClick={() => downloadDocument(doc)}
                              className="text-green-600 hover:text-green-800 ml-1"
                              title="İndir"
                            >
                              <Download className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setProgressModalOpen(false)}>
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Görevi Tamamla Modalı */}
      <Dialog open={completeModalOpen} onOpenChange={setCompleteModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Görevi Tamamla
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="font-medium">{selectedTask?.title}</p>
              <p className="text-sm text-gray-500">{selectedTask?.description}</p>
            </div>
            
            <div className="bg-blue-50 p-3 rounded-lg text-blue-700 text-sm">
              <p className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Tamamlanma tarihi ve saati otomatik olarak kaydedilecektir.
              </p>
            </div>
            
            <div>
              <Label htmlFor="completion-notes">Tamamlama Açıklaması *</Label>
              <Textarea 
                id="completion-notes"
                value={completionNotes}
                onChange={(e) => setCompletionNotes(e.target.value)}
                placeholder="Görevin nasıl tamamlandığını açıklayın..."
                rows={4}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteModalOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleCompleteTask}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Tamamla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Görevi Bitir Bildirimi Modalı - başkasının görevi için */}
      <Dialog open={finishNotifyModalOpen} onOpenChange={setFinishNotifyModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-amber-600" />
              Görev Bitirme Bildirimi
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="font-medium">{selectedTask?.title}</p>
              <p className="text-sm text-gray-500">{selectedTask?.description}</p>
            </div>
            
            <div className="bg-amber-50 p-3 rounded-lg text-amber-700 text-sm">
              <p className="flex items-center gap-2">
                <Send className="h-4 w-4" />
                Bu işlem görevi tamamlamaz, sadece görevi oluşturan kişiye bildirim gönderir.
              </p>
            </div>
            
            <div>
              <Label htmlFor="finish-notes">Bitirme Açıklaması *</Label>
              <Textarea 
                id="finish-notes"
                value={finishNotifyNotes}
                onChange={(e) => setFinishNotifyNotes(e.target.value)}
                placeholder="Görevi nasıl tamamladığınızı açıklayın..."
                rows={4}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setFinishNotifyModalOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleFinishNotify}
              className="bg-amber-600 hover:bg-amber-700"
            >
              <Send className="h-4 w-4 mr-2" />
              Bildirimi Gönder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Belge Önizleme Modalı */}
      <Dialog open={!!previewDoc} onOpenChange={() => setPreviewDoc(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{previewDoc?.name}</DialogTitle>
          </DialogHeader>
          <div className="flex justify-center items-center min-h-[400px]">
            {previewDoc?.type?.startsWith('image/') ? (
              <img src={previewDoc?.data} alt={previewDoc?.name} className="max-w-full max-h-[70vh] object-contain" />
            ) : previewDoc?.type === 'application/pdf' ? (
              <iframe src={previewDoc?.data} title={previewDoc?.name} className="w-full h-[70vh]" />
            ) : (
              <p>Bu dosya türü önizlenemiyor</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewDoc(null)}>
              Kapat
            </Button>
            <Button onClick={() => downloadDocument(previewDoc)}>
              <Download className="h-4 w-4 mr-2" />
              İndir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Görev Silme Onay Modalı */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Görevleri Sil
            </DialogTitle>
            <DialogDescription>
              {selectedTasks.length} adet görevi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
              İptal
            </Button>
            <Button variant="destructive" onClick={handleDeleteSelectedTasks} disabled={deleting}>
              {deleting ? 'Siliniyor...' : `Evet, ${selectedTasks.length} Görevi Sil`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BranchTasks2;
